import React from 'react';

import { FarIcons } from '@playbooks/icons';
import { Feedback } from '@playbooks/molecules/feedbacks';
import { Skeleton } from '@playbooks/molecules/skeletons';
import { Button } from '@playbooks/ui/components/button';
import { RadioGroup, RadioGroupItem } from '@playbooks/ui/components/radio-group';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';
import { cn } from '@playbooks/ui/lib/utils';

const ComposerActionHeader = ({ title = '', children = null, onPrev }) => (
	<header className='flex-start gap-2 border-b p-2'>
		{onPrev ? (
			<Button variant='ghost' size='icon-sm' aria-label='Back' onClick={onPrev}>
				<StructuralIcon icon={FarIcons.farChevronLeft} aria-hidden />
			</Button>
		) : null}
		{children || <span className='type-label'>{title}</span>}
	</header>
);

const ComposerActionSection = ({ label = '', children, className = '' }) => {
	// Theme
	const rootClassName = cn('grid gap-0.5 p-1', className);

	// Render
	return (
		<section className={rootClassName}>
			{label ? <p className='type-pretitle px-2 py-1'>{label}</p> : null}
			<div className='grid gap-0.5'>{children}</div>
		</section>
	);
};

const ComposerActionRow = ({
	icon = null,
	label = '',
	value = '',
	selected = false,
	trailing = undefined,
	className = '',
	children = null,
	...props
}) => {
	// Theme
	const rootClassName = cn('h-auto min-h-9 w-full justify-start gap-2 px-2.5 py-2 text-left', className);

	// Render
	return (
		<Button variant='ghost' size='sm' data-active={selected || undefined} {...props} className={rootClassName}>
			{icon ? (
				<StructuralIcon data-icon='inline-start' icon={icon} className='size-3.5 text-foreground-body' aria-hidden />
			) : null}
			<span className='min-w-0 grow truncate'>{children || label}</span>
			{value ? <span className='max-w-28 truncate text-xs font-normal text-foreground-body'>{value}</span> : null}
			{trailing !== undefined ? (
				trailing
			) : (
				<StructuralIcon
					data-icon='inline-end'
					icon={FarIcons.farChevronRight}
					className='size-3 text-foreground-body'
					aria-hidden
				/>
			)}
		</Button>
	);
};

const ComposerActionListSkeleton = ({ count = 4 }) => (
	<div className='grid gap-0.5 p-1' aria-label='Loading options'>
		<Skeleton layout='item' count={count} className='min-h-9 border-0 px-2.5 py-1' />
	</div>
);

const ComposerActionFeedback = ({ className = '', ...props }) => (
	<Feedback layout='drop' {...props} className={cn('m-2 rounded-panel border border-border bg-surface', className)} />
);

const ComposerActionRadioGroup = ({ idPrefix, value, options = [], disabled = false, onValueChange }) => (
	<RadioGroup value={value} onValueChange={onValueChange} className='gap-0.5 p-1' disabled={disabled}>
		{options.map(option => {
			const id = `${idPrefix}-${option.value}`;
			return (
				<label
					key={option.value}
					htmlFor={id}
					className='flex min-h-12 cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2 hover:bg-surface-hover has-data-checked:bg-surface-selected has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50'>
					<RadioGroupItem id={id} value={option.value} className='mt-0.5' />
					<span className='grid min-w-0 grow gap-0.5'>
						<span className='text-sm font-medium text-foreground'>{option.label}</span>
						<span className='text-xs leading-4 font-normal text-foreground-subtle'>{option.description}</span>
					</span>
				</label>
			);
		})}
	</RadioGroup>
);

export {
	ComposerActionHeader,
	ComposerActionFeedback,
	ComposerActionListSkeleton,
	ComposerActionRadioGroup,
	ComposerActionRow,
	ComposerActionSection,
};
